import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useLocation, useParams } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiAward, FiPlus, FiSave, FiShuffle } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { useAdminAccess } from '../utilities/adminAccess';
import {
  applicationOptionLabel,
  buildTiebreakerItems,
  champFields,
  DEFAULT_TABLE_TIEBREAKER_PRESETS,
  deriveGroupsFromServer,
  teamInGroup,
  enabledTiebreakerCodes,
  extractEntity,
  extractList,
  fromDateTimeLocalValue,
  isEnabled,
  normalizeSeasonTeam,
  normalizeTableTiebreakers,
  parseTableTiebreakersDictionary,
  sameTiebreakerOrder,
  toDateTimeLocalValue,
} from '../utilities/tournamentsApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import SeasonRoster from '../components/SeasonRoster';
import SeasonActions from '../components/SeasonActions';
import SeasonTiebreakers from '../components/SeasonTiebreakers';
import CustomModal from '../components/CustomModal';
import '../styles/createMatchModal.css';
import '../styles/stadiumsManagement.css';
import '../styles/disqualificationsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/seasonRoster.css';

const RATE_RATIOS = [20, 30, 40, 50, 60];

const emptyForm = {
  show: true,
  name: '',
  inProgress: false,
  startingAt: '',
  endingAt: '',
  league_type: '1',
  rate_ratio: '',
  penalty_draw_score: '',
  application_id: '',
  application_active: false,
  group_stage_amount: '',
};

const emptyTiebreakersState = () => ({
  criteria: DEFAULT_TABLE_TIEBREAKER_PRESETS[1].map((code, index) => ({
    code,
    name: '',
    priority: index + 1,
  })),
  presets: {
    1: [...DEFAULT_TABLE_TIEBREAKER_PRESETS[1]],
    2: [...DEFAULT_TABLE_TIEBREAKER_PRESETS[2]],
  },
  items: buildTiebreakerItems(
    DEFAULT_TABLE_TIEBREAKER_PRESETS[1].map((code) => ({ code, name: '' })),
    DEFAULT_TABLE_TIEBREAKER_PRESETS[1],
  ),
  savedCustom: null,
  savedEffective: [...DEFAULT_TABLE_TIEBREAKER_PRESETS[1]],
  sendNull: true,
});

/** Soft API failure on HTTP 200: { err }, { success: false }, { answer: false }. */
const getSoftApiErrorText = (data) => {
  if (!data || typeof data !== 'object') return '';
  if (typeof data.err === 'string' && data.err.trim()) return data.err.trim();
  if (typeof data.error === 'string' && data.error.trim()) return data.error.trim();
  if (Object.prototype.hasOwnProperty.call(data, 'success') && data.success === false) {
    return (typeof data.message === 'string' && data.message.trim()) || 'error';
  }
  if (Object.prototype.hasOwnProperty.call(data, 'answer') && !data.answer) {
    return (typeof data.message === 'string' && data.message.trim()) || 'error';
  }
  return '';
};

const throwIfSoftApiError = (response, fallback) => {
  const text = getSoftApiErrorText(response?.data);
  if (!text) return;
  const error = new Error(text === 'error' ? fallback : text);
  error.response = { status: response?.status || 200, data: response?.data };
  throw error;
};

export default function ManageSeason() {
  const { countryId, leagueId, seasonId } = useParams();
  const isNew = !seasonId;
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useI18n();
  const { champId, loading: champsLoading } = useChampionships();
  const { canWrite } = useAdminAccess('tournaments');
  const leaguesAccess = useAdminAccess('leagues');

  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [leagueName, setLeagueName] = useState('');
  const [countryName, setCountryName] = useState('');
  const [applications, setApplications] = useState([]);
  const [stages, setStages] = useState([]);
  const [originalStages, setOriginalStages] = useState([]);
  const [reorderMode, setReorderMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isFirstSeason, setIsFirstSeason] = useState(false);
  const [savedRoster, setSavedRoster] = useState([]);
  const [rosterDraft, setRosterDraft] = useState([]);
  const [savedGroups, setSavedGroups] = useState({ list: [], assignment: {} });
  const [groupsDraft, setGroupsDraft] = useState({ list: [], assignment: {} });
  const [rosterLoading, setRosterLoading] = useState(false);
  const [savingRoster, setSavingRoster] = useState(false);
  const [moveVisible, setMoveVisible] = useState(false);
  const [moveLeagues, setMoveLeagues] = useState([]);
  const [moveTargetId, setMoveTargetId] = useState('');
  const [moveLoading, setMoveLoading] = useState(false);
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState('');
  const [tiebreakers, setTiebreakers] = useState(emptyTiebreakersState);
  // Avoid re-init on champsLoading flicker — it was wiping success/error banners after save
  const loadedKeyRef = useRef('');

  useEffect(() => {
    if (!champId && champsLoading) return;
    const key = `${countryId}|${leagueId}|${seasonId || 'new'}|${champId || ''}`;
    if (champId && loadedKeyRef.current === key) return;
    loadedKeyRef.current = key;
    initPage();
  }, [countryId, leagueId, seasonId, champId, champsLoading]);

  const applyTiebreakersOrder = (order, { sendNull = false, criteria, presets } = {}) => {
    setTiebreakers((prev) => {
      const nextCriteria = criteria || prev.criteria;
      const nextPresets = presets || prev.presets;
      return {
        ...prev,
        criteria: nextCriteria,
        presets: nextPresets,
        items: buildTiebreakerItems(nextCriteria, order),
        sendNull,
      };
    });
  };

  const initPage = async () => {
    setErrors({});
    setReorderMode(false);
    setTiebreakers(emptyTiebreakersState());

    const incoming = location.state || {};
    const hasNavFlash = Boolean(incoming.saveFlash?.text || incoming.moveSuccessLeague);
    // Keep nav flash; otherwise clear so a previous page's banner doesn't linger
    if (!hasNavFlash) {
      setMessage({ text: '', type: '' });
    }

    if (!champId) {
      setLoading(false);
      setMessage({ text: t('championshipsLoadError'), type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const [countriesResponse, leagueResponse, applicationsResponse, tiebreakersResponse] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getAdminCountries, { champ_id: champId }),
        authorizedPost(dispatch, API_URLS.getAdminLeague, { id: Number(leagueId) }),
        authorizedPost(dispatch, API_URLS.getAdminApplications, { champ_id: champId }),
        authorizedPost(dispatch, API_URLS.getTableTiebreakers, {}).catch(() => ({ data: null })),
      ]);

      const dictionary = parseTableTiebreakersDictionary(tiebreakersResponse.data);

      const countries = extractList(countriesResponse.data);
      const country = countries.find((item) => Number(item.id) === Number(countryId));
      setCountryName(country?.name || '');

      const league = extractEntity(leagueResponse.data);
      setLeagueName(league.name || '');
      setApplications(extractList(applicationsResponse.data));
      const existingSeasons = extractList(league.seasons);
      const firstSeason = isNew && existingSeasons.length === 0;
      setIsFirstSeason(firstSeason);
      const selectedApplicationId = incoming.selectedApplicationId;
      const draft = incoming.seasonDraft || {};

      if (
        incoming.selectedApplicationId
        || incoming.seasonDraft
        || incoming.moveSuccessLeague
        || incoming.saveFlash
      ) {
        history.replace({
          pathname: location.pathname,
          search: location.search,
          state: {},
        });
      }

      if (incoming.saveFlash?.text) {
        setMessage({
          text: String(incoming.saveFlash.text),
          type: incoming.saveFlash.type === 'error' ? 'error' : 'success',
        });
      } else if (incoming.moveSuccessLeague) {
        setMessage({
          text: t('trMoveSeasonSuccess', { league: incoming.moveSuccessLeague }),
          type: 'success',
        });
      }

      if (isNew) {
        const draftOrder = Array.isArray(draft.table_tiebreakers)
          ? draft.table_tiebreakers
          : dictionary.presets[1];
        const draftSendNull = draft.table_tiebreakers_send_null != null
          ? Boolean(draft.table_tiebreakers_send_null)
          : !Array.isArray(draft.table_tiebreakers);
        setForm({
          ...emptyForm,
          ...draft,
          inProgress: firstSeason ? true : Boolean(draft.inProgress),
          application_id: selectedApplicationId
            ? String(selectedApplicationId)
            : (draft.application_id ? String(draft.application_id) : ''),
        });
        setTiebreakers({
          criteria: dictionary.criteria,
          presets: dictionary.presets,
          items: buildTiebreakerItems(dictionary.criteria, draftOrder),
          savedCustom: null,
          savedEffective: [...dictionary.presets[1]],
          sendNull: draftSendNull,
        });
        setStages([]);
        setOriginalStages([]);
        setSavedRoster([]);
        setRosterDraft([]);
        setSavedGroups({ list: [], assignment: {} });
        setGroupsDraft({ list: [], assignment: {} });
        return;
      }

      const response = await authorizedPost(dispatch, API_URLS.getAdminSeason, { id: Number(seasonId) });
      const season = extractEntity(response.data);
      const settings = season.league_settings || {};
      const savedCustom = normalizeTableTiebreakers(settings.table_tiebreakers);
      const savedEffective = normalizeTableTiebreakers(settings.table_tiebreakers_effective)
        || normalizeTableTiebreakers(draft.table_tiebreakers)
        || [...dictionary.presets[1]];
      const draftOrder = Array.isArray(draft.table_tiebreakers) ? draft.table_tiebreakers : savedEffective;
      const draftSendNull = draft.table_tiebreakers_send_null != null
        ? Boolean(draft.table_tiebreakers_send_null)
        : false;
      setForm({
        show: draft.show != null ? Boolean(draft.show) : isEnabled(season.show),
        name: draft.name != null ? draft.name : (season.name || ''),
        inProgress: draft.inProgress != null ? Boolean(draft.inProgress) : isEnabled(season.inProgress),
        startingAt: draft.startingAt != null ? draft.startingAt : toDateTimeLocalValue(season.startingAt),
        endingAt: draft.endingAt != null ? draft.endingAt : toDateTimeLocalValue(season.endingAt),
        league_type: draft.league_type != null ? draft.league_type : String(settings.league_type ?? 1),
        rate_ratio: draft.rate_ratio != null
          ? draft.rate_ratio
          : (settings.rate_ratio == null ? '' : String(settings.rate_ratio)),
        penalty_draw_score: draft.penalty_draw_score != null
          ? draft.penalty_draw_score
          : (settings.penalty_draw_score == null ? '' : String(settings.penalty_draw_score)),
        application_id: selectedApplicationId
          ? String(selectedApplicationId)
          : (draft.application_id != null
            ? String(draft.application_id)
            : (settings.application_id ? String(settings.application_id) : '')),
        application_active: draft.application_active != null
          ? Boolean(draft.application_active)
          : isEnabled(settings.application_active),
        group_stage_amount: draft.group_stage_amount != null
          ? draft.group_stage_amount
          : (settings.group_stages_amount == null && settings.group_stage_amount == null
            ? ''
            : String(settings.group_stages_amount ?? settings.group_stage_amount)),
      });
      setTiebreakers({
        criteria: dictionary.criteria,
        presets: dictionary.presets,
        items: buildTiebreakerItems(dictionary.criteria, draftOrder),
        savedCustom,
        savedEffective,
        sendNull: draftSendNull,
      });
      const nextStages = extractList(season.stages);
      setStages(nextStages);
      setOriginalStages(nextStages);

      setRosterLoading(true);
      try {
        const [teamsResponse, groupsResponse] = await Promise.all([
          authorizedPost(dispatch, API_URLS.getAdminSeasonTeams, {
            ...champFields(champId),
            league_id: Number(leagueId),
            season_id: Number(seasonId),
          }).catch((error) => {
            if (!hasNavFlash) {
              setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
            }
            return { data: [] };
          }),
          Number(settings.league_type) === 2
            ? authorizedPost(dispatch, API_URLS.getAdminSeasonGroups, {
              ...champFields(champId),
              league_id: Number(leagueId),
              season_id: Number(seasonId),
            }).catch((error) => {
              if (!hasNavFlash) {
                setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
              }
              return { data: [] };
            })
            : Promise.resolve({ data: [] }),
        ]);
        const nextRoster = extractList(teamsResponse.data).map(normalizeSeasonTeam);
        const nextGroups = deriveGroupsFromServer(groupsResponse.data);
        setSavedRoster(nextRoster);
        setRosterDraft(nextRoster);
        setSavedGroups(nextGroups);
        setGroupsDraft(nextGroups);
      } finally {
        setRosterLoading(false);
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) {
      setErrors((prev) => ({ ...prev, [key]: '' }));
    }
  };

  const lockInProgress = isNew && isFirstSeason;

  const seasonDraftPayload = () => ({
    ...form,
    table_tiebreakers: enabledTiebreakerCodes(tiebreakers.items),
    table_tiebreakers_send_null: tiebreakers.sendNull,
  });

  const persistSeasonDraft = () => {
    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: { seasonDraft: seasonDraftPayload() },
    });
  };

  const goCreateApplication = () => {
    const draft = seasonDraftPayload();
    history.replace({
      pathname: location.pathname,
      search: location.search,
      state: { seasonDraft: draft },
    });
    history.push({
      pathname: '/tournaments/applications/new',
      state: {
        returnTo: `${location.pathname}${location.search}`,
        seasonDraft: draft,
      },
    });
  };

  const goManageApplications = () => {
    persistSeasonDraft();
    history.push('/tournaments/applications');
  };

  const roster = rosterDraft;
  const groupsState = groupsDraft;

  const rosterDirty = useMemo(() => {
    if (roster.length !== savedRoster.length) return true;
    return roster.some((team, index) => team.team_id !== savedRoster[index]?.team_id);
  }, [roster, savedRoster]);

  const groupsDirty = useMemo(
    () => JSON.stringify(groupsState) !== JSON.stringify(savedGroups),
    [groupsState, savedGroups]
  );

  const saveRoster = async () => {
    setSavingRoster(true);
    setMessage({ text: '', type: '' });
    try {
      const snapshot = roster.map(normalizeSeasonTeam);
      const response = await authorizedPost(dispatch, API_URLS.setSeasonTeams, {
        ...champFields(champId),
        league_id: Number(leagueId),
        season_id: Number(seasonId),
        teams: snapshot.map((team) => team.team_id),
      });
      throwIfSoftApiError(response, t('stSaveError'));
      setSavedRoster(snapshot);
      setRosterDraft(snapshot);
      setMessage({ text: t('trTeamsSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSavingRoster(false);
    }
  };

  const saveGroups = async () => {
    setSavingRoster(true);
    setMessage({ text: '', type: '' });
    try {
      const snapshot = groupsState;
      const response = await authorizedPost(dispatch, API_URLS.setSeasonGroups, {
        ...champFields(champId),
        league_id: Number(leagueId),
        season_id: Number(seasonId),
        groups: snapshot.list.map((group) => ({
          group_id: group.serverId != null ? group.serverId : null,
          group_name: group.name,
          priority: group.priority,
          teams: roster
            .filter((team) => teamInGroup(snapshot.assignment, team.team_id, group.id))
            .map((team) => team.team_id),
        })),
      });
      throwIfSoftApiError(response, t('stSaveError'));
      setSavedGroups(snapshot);
      setGroupsDraft(snapshot);
      setMessage({ text: t('trGroupsSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSavingRoster(false);
    }
  };

  const reloadSeasonTeams = async () => {
    setRosterLoading(true);
    try {
      const [teamsResponse, groupsResponse] = await Promise.all([
        authorizedPost(dispatch, API_URLS.getAdminSeasonTeams, {
          ...champFields(champId),
          league_id: Number(leagueId),
          season_id: Number(seasonId),
        }),
        Number(form.league_type) === 2
          ? authorizedPost(dispatch, API_URLS.getAdminSeasonGroups, {
            ...champFields(champId),
            league_id: Number(leagueId),
            season_id: Number(seasonId),
          })
          : Promise.resolve({ data: [] }),
      ]);
      const nextRoster = extractList(teamsResponse.data).map(normalizeSeasonTeam);
      const nextGroups = deriveGroupsFromServer(groupsResponse.data);
      setSavedRoster(nextRoster);
      setRosterDraft(nextRoster);
      setSavedGroups(nextGroups);
      setGroupsDraft(nextGroups);
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stLoadError')), type: 'error' });
    } finally {
      setRosterLoading(false);
    }
  };

  const currentTiebreakerCodes = useMemo(
    () => enabledTiebreakerCodes(tiebreakers.items),
    [tiebreakers.items],
  );

  const tiebreakersDirty = useMemo(() => {
    if (tiebreakers.sendNull) {
      return tiebreakers.savedCustom != null;
    }
    if (tiebreakers.savedCustom == null) {
      return !sameTiebreakerOrder(currentTiebreakerCodes, tiebreakers.savedEffective);
    }
    return !sameTiebreakerOrder(currentTiebreakerCodes, tiebreakers.savedCustom);
  }, [
    currentTiebreakerCodes,
    tiebreakers.savedCustom,
    tiebreakers.savedEffective,
    tiebreakers.sendNull,
  ]);

  const buildPayload = () => {
    const payload = {
      name: form.name.trim(),
      show: form.show ? 1 : 0,
      inProgress: lockInProgress || form.inProgress ? 1 : 0,
      startingAt: fromDateTimeLocalValue(form.startingAt) || null,
      endingAt: fromDateTimeLocalValue(form.endingAt) || null,
      champ_id: champId,
      championat_id: champId,
      league_id: Number(leagueId),
      country_id: Number(countryId),
      league_type: Number(form.league_type) || 1,
      rate_ratio: form.rate_ratio === '' ? null : Number(form.rate_ratio),
      // пусто → null (правило выкл.); иначе строго number, не строка
      penalty_draw_score: form.penalty_draw_score === '' || form.penalty_draw_score == null
        ? null
        : Number(form.penalty_draw_score),
      application_id: Number(form.application_id),
      application_active: form.application_active ? 1 : 0,
      group_stage_amount: String(form.league_type) === '2' && form.group_stage_amount !== ''
        ? Number(form.group_stage_amount)
        : null,
    };

    if (isNew) {
      payload.table_tiebreakers = tiebreakers.sendNull ? null : currentTiebreakerCodes;
    } else if (tiebreakersDirty) {
      payload.table_tiebreakers = tiebreakers.sendNull ? null : currentTiebreakerCodes;
    }

    return payload;
  };

  const handleSave = async () => {
    const nextErrors = {};
    if (!form.name.trim()) {
      nextErrors.name = t('stdNameRequired');
    }
    const applicationId = Number(form.application_id);
    if (!form.application_id || !Number.isFinite(applicationId) || applicationId <= 0) {
      nextErrors.application_id = t('trApplicationRequired');
    }
    if (form.penalty_draw_score !== '' && form.penalty_draw_score != null) {
      const penaltyScore = Number(form.penalty_draw_score);
      if (!Number.isFinite(penaltyScore) || penaltyScore <= 0) {
        nextErrors.penalty_draw_score = t('trPenaltyDrawScoreInvalid');
      }
    }
    if (String(form.league_type) === '2') {
      const amount = Number(form.group_stage_amount);
      if (form.group_stage_amount === '' || Number.isNaN(amount) || amount < 1) {
        nextErrors.group_stage_amount = t('trGroupStagesAmountRequired');
      }
    }
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setMessage({
        text: Object.values(nextErrors).find(Boolean) || t('stSaveError'),
        type: 'error',
      });
      return;
    }

    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const payload = buildPayload();
      if (isNew) {
        const response = await authorizedPost(dispatch, API_URLS.addSeason, payload);
        throwIfSoftApiError(response, t('stSaveError'));
        const created = extractEntity(response.data);
        const flash = { text: t('trSeasonCreated'), type: 'success' };
        setMessage(flash);
        if (created.id) {
          // Survive remount after replace (initPage would clear local message)
          history.replace({
            pathname: `/tournaments/${countryId}/league/${leagueId}/season/${created.id}`,
            state: { saveFlash: flash },
          });
        } else {
          history.push(`/tournaments/${countryId}/league/${leagueId}`);
        }
      } else {
        const response = await authorizedPost(dispatch, API_URLS.changeSeason, {
          ...payload,
          id: Number(seasonId),
        });
        throwIfSoftApiError(response, t('stSaveError'));
        const updated = extractEntity(response.data);
        const settings = updated.league_settings || {};
        if (updated.id) {
          setForm((prev) => ({
            ...prev,
            startingAt: toDateTimeLocalValue(updated.startingAt),
            endingAt: toDateTimeLocalValue(updated.endingAt),
            ...(Object.prototype.hasOwnProperty.call(settings, 'penalty_draw_score')
              ? {
                penalty_draw_score: settings.penalty_draw_score == null
                  ? ''
                  : String(settings.penalty_draw_score),
              }
              : {}),
          }));
        }
        if (Object.keys(settings).length) {
          const savedCustom = normalizeTableTiebreakers(settings.table_tiebreakers);
          const savedEffective = normalizeTableTiebreakers(settings.table_tiebreakers_effective)
            || currentTiebreakerCodes;
          setTiebreakers((prev) => ({
            ...prev,
            items: buildTiebreakerItems(prev.criteria, savedEffective),
            savedCustom,
            savedEffective,
            sendNull: savedCustom == null,
          }));
        } else if (tiebreakersDirty) {
          setTiebreakers((prev) => ({
            ...prev,
            savedCustom: prev.sendNull ? null : currentTiebreakerCodes,
            savedEffective: currentTiebreakerCodes,
            sendNull: prev.sendNull,
          }));
        }
        setMessage({ text: t('stSaved'), type: 'success' });
      }
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stSaveError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const closeMoveModal = () => {
    if (moving) return;
    setMoveVisible(false);
    setMoveTargetId('');
    setMoveError('');
  };

  const openMoveModal = async () => {
    setMoveVisible(true);
    setMoveTargetId('');
    setMoveError('');
    setMoveLoading(true);
    try {
      const response = await authorizedPost(dispatch, API_URLS.getAdminCountry, { id: Number(countryId) });
      const country = extractEntity(response.data);
      const leagues = extractList(country.leagues).filter(
        (item) => Number(item.id) !== Number(leagueId),
      );
      setMoveLeagues(leagues);
    } catch (error) {
      setMoveLeagues([]);
      setMoveError(getApiErrorMessage(error, t('stLoadError')));
    } finally {
      setMoveLoading(false);
    }
  };

  const moveTargetLeague = useMemo(
    () => moveLeagues.find((item) => String(item.id) === String(moveTargetId)) || null,
    [moveLeagues, moveTargetId],
  );

  const handleMoveSeason = async () => {
    if (!moveTargetLeague || moving) return;
    setMoving(true);
    setMoveError('');
    try {
      const response = await authorizedPost(dispatch, API_URLS.moveSeason, {
        season_id: Number(seasonId),
        to_league_id: Number(moveTargetLeague.id),
      });
      throwIfSoftApiError(response, t('trMoveSeasonError'));
      const moved = extractEntity(response.data);
      const nextLeagueId = Number(moved.league_id || moveTargetLeague.id);
      const nextLeagueName = moveTargetLeague.name || String(nextLeagueId);
      setMoveVisible(false);
      setMoveTargetId('');
      history.replace({
        pathname: `/tournaments/${countryId}/league/${nextLeagueId}/season/${seasonId}`,
        state: { moveSuccessLeague: nextLeagueName },
      });
    } catch (error) {
      setMoveError(getApiErrorMessage(error, t('trMoveSeasonError')));
    } finally {
      setMoving(false);
    }
  };

  const handleSaveStageOrder = async () => {
    setSavingOrder(true);
    setMessage({ text: '', type: '' });
    try {
      const response = await authorizedPost(dispatch, API_URLS.changeStagesPriority, {
        stages: stages.map((item, index) => ({ id: item.id, sortIdx: index })),
      });
      throwIfSoftApiError(response, t('stOrderError'));
      const ordered = stages.map((item, index) => ({ ...item, sortIdx: index }));
      setStages(ordered);
      setOriginalStages(ordered);
      setReorderMode(false);
      setMessage({ text: t('stOrderSaved'), type: 'success' });
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('stOrderError')), type: 'error' });
    } finally {
      setSavingOrder(false);
    }
  };

  const stageColumns = [
    {
      key: 'stage_name',
      label: t('stdName'),
      className: 'st-cell-name',
      render: (item) => {
        const name = item.stage_name || item.short_stage_name || '—';
        return isEnabled(item.isPlayoff) ? `${name} · ${t('stPlayoff')}` : name;
      },
    },
    {
      key: 'sortIdx',
      label: t('stdPriority'),
      className: 'st-cell-priority',
      render: (item, index) => (
        <span className="st-priority-badge">{reorderMode ? index + 1 : (item.sortIdx ?? item.priority ?? index + 1)}</span>
      ),
    },
  ];

  const crumbs = [
    { label: t('navTournaments'), to: '/tournaments' },
    { label: countryName || t('trCountry'), to: `/tournaments/${countryId}` },
    { label: leagueName || t('trEditLeague'), to: `/tournaments/${countryId}/league/${leagueId}` },
    { label: isNew ? t('trNewSeason') : (form.name.trim() || t('trEditSeason')) },
  ];

  const seasonForm = (
    <div className="st-form-grid">
      <div className="st-field">
        <label className="st-check">
          <input
            type="checkbox"
            checked={form.show}
            onChange={(event) => updateField('show', event.target.checked)}
          />
          {t('stShowOnSite')}
        </label>
      </div>

      <div className="st-field">
        <label className={`st-check ${lockInProgress ? 'is-disabled' : ''}`}>
          <input
            type="checkbox"
            checked={lockInProgress ? true : form.inProgress}
            disabled={lockInProgress}
            onChange={(event) => updateField('inProgress', event.target.checked)}
          />
          {t('trInProgress')}
        </label>
        {lockInProgress ? <span className="st-photo-hint">{t('trFirstSeasonLocked')}</span> : null}
      </div>

      <div className="st-field">
        <label>{t('stdName')} <span className="st-required">*</span></label>
        <input
          className={`st-input ${errors.name ? 'error' : ''}`}
          value={form.name}
          placeholder={t('trSeasonNamePh')}
          onChange={(event) => updateField('name', event.target.value)}
        />
        {errors.name ? <span className="st-field-error">{errors.name}</span> : null}
      </div>

      <div className="st-field">
        <label>{t('trRateRatio')}</label>
        <select
          className="st-select"
          value={form.rate_ratio}
          onChange={(event) => updateField('rate_ratio', event.target.value)}
        >
          <option value="">{t('trRateRatioPlaceholder')}</option>
          {Array.from(new Set([
            ...RATE_RATIOS,
            form.rate_ratio === '' ? null : Number(form.rate_ratio),
          ].filter((value) => value != null && !Number.isNaN(value)))).sort((a, b) => a - b).map((value) => (
            <option key={value} value={String(value)}>{value}</option>
          ))}
        </select>
        <span className="st-photo-hint">{t('trRateRatioHint')}</span>
      </div>

      <div className="st-field">
        <label>{t('trPenaltyDrawScore')}</label>
        <input
          type="number"
          min="1"
          step="1"
          className={`st-input ${errors.penalty_draw_score ? 'error' : ''}`}
          value={form.penalty_draw_score}
          placeholder={t('trPenaltyDrawScorePh')}
          onChange={(event) => updateField('penalty_draw_score', event.target.value)}
        />
        {errors.penalty_draw_score ? (
          <span className="st-field-error">{errors.penalty_draw_score}</span>
        ) : null}
        <span className="st-photo-hint">{t('trPenaltyDrawScoreHint')}</span>
      </div>

      <div className="st-field">
        <label>{t('trStartingAt')}</label>
        <input
          type="datetime-local"
          className="st-input"
          value={form.startingAt}
          onChange={(event) => updateField('startingAt', event.target.value)}
        />
      </div>

      <div className="st-field">
        <label>{t('trEndingAt')}</label>
        <input
          type="datetime-local"
          className="st-input"
          value={form.endingAt}
          onChange={(event) => updateField('endingAt', event.target.value)}
        />
      </div>

      <div className="st-field">
        <label>{t('trLeagueType')}</label>
        <select
          className="st-select"
          value={form.league_type}
          onChange={(event) => updateField('league_type', event.target.value)}
        >
          <option value="1">{t('trLeagueTypeRegular')}</option>
          <option value="2">{t('trLeagueTypeGroups')}</option>
        </select>
      </div>

      <div className="st-field st-field-wide">
        <SeasonTiebreakers
          items={tiebreakers.items}
          dirty={tiebreakersDirty}
          sendNull={tiebreakers.sendNull && tiebreakers.savedCustom != null}
          disabled={!canWrite}
          onItemsChange={(nextItems) => {
            setTiebreakers((prev) => ({
              ...prev,
              items: nextItems,
              sendNull: false,
            }));
          }}
          onApplyPreset={(presetId) => {
            const order = tiebreakers.presets[presetId] || tiebreakers.presets[1];
            applyTiebreakersOrder(order, { sendNull: false });
          }}
          onReset={() => {
            const order = tiebreakers.savedCustom == null
              ? tiebreakers.savedEffective
              : (tiebreakers.presets[1] || tiebreakers.savedEffective);
            applyTiebreakersOrder(order, { sendNull: true });
          }}
        />
      </div>

      <div className="st-field st-field-wide">
        <label>{t('trApplication')} <span className="st-required">*</span></label>
        <div className="tr-field-with-action">
          <select
            className={`st-select ${errors.application_id ? 'error' : ''}`}
            value={String(form.application_id || '')}
            onChange={(event) => updateField('application_id', event.target.value)}
            required
          >
            <option value="">{t('trApplicationPlaceholder')}</option>
            {applications
              .filter((item) => isEnabled(item.active) || String(item.id) === String(form.application_id))
              .map((item) => {
                const label = applicationOptionLabel(item) || String(item.id);
                const inactive = !isEnabled(item.active);
                return (
                  <option key={item.id} value={String(item.id)}>
                    {inactive ? `${label} (${t('trApplicationInactiveMark')})` : label}
                  </option>
                );
              })}
          </select>
          <button
            type="button"
            className="st-btn st-btn-secondary"
            onClick={goCreateApplication}
          >
            <FiPlus /> {t('trCreateApplication')}
          </button>
        </div>
        {errors.application_id ? <span className="st-field-error">{errors.application_id}</span> : null}
        <button
          type="button"
          className="tr-inline-link"
          onClick={goManageApplications}
        >
          {t('trManageApplications')}
        </button>
      </div>

      <div className="st-field">
        <label className="st-check">
          <input
            type="checkbox"
            checked={form.application_active}
            onChange={(event) => updateField('application_active', event.target.checked)}
          />
          {t('trApplicationActive')}
        </label>
        <span className="st-photo-hint">{t('trApplicationActiveHint')}</span>
      </div>

      {String(form.league_type) === '2' ? (
        <div className="st-field st-field-wide">
          <label>{t('trGroupStagesAmount')} <span className="st-required">*</span></label>
          <input
            type="number"
            min="1"
            className={`st-input ${errors.group_stage_amount ? 'error' : ''}`}
            value={form.group_stage_amount}
            onChange={(event) => updateField('group_stage_amount', event.target.value)}
          />
          {errors.group_stage_amount ? <span className="st-field-error">{errors.group_stage_amount}</span> : null}
          <span className="st-photo-hint">{t('trGroupStagesAmountHint')}</span>
        </div>
      ) : null}
    </div>
  );

  const stageActions = reorderMode ? (
    <>
      <button
        type="button"
        className="st-btn st-btn-secondary"
        disabled={savingOrder}
        onClick={() => {
          setStages(originalStages);
          setReorderMode(false);
        }}
      >
        {t('stCancelReorder')}
      </button>
      <button
        type="button"
        className="st-btn st-btn-primary"
        disabled={savingOrder || !stages.length}
        onClick={handleSaveStageOrder}
      >
        <FiSave /> {t('stSaveOrder')}
      </button>
    </>
  ) : (
    <>
      <button
        type="button"
        className="st-btn st-btn-secondary"
        disabled={loading || stages.length < 2}
        onClick={() => {
          setOriginalStages(stages);
          setReorderMode(true);
        }}
      >
        <span className="tr-btn-full">{t('stReorder')}</span>
        <span className="tr-btn-short">{t('stReorderShort')}</span>
      </button>
      <Link
        to={`/tournaments/${countryId}/league/${leagueId}/season/${seasonId}/stage/new`}
        className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
        aria-disabled={loading || undefined}
        onClick={(event) => { if (loading) event.preventDefault(); }}
      >
        <FiPlus />
        <span className="tr-btn-full">{t('trAddStage')}</span>
        <span className="tr-btn-short">{t('trAddStageShort')}</span>
      </Link>
    </>
  );

  if (isNew) {
    return (
      <div className="layoutWrapper st-layout">
        <div className="matchCenterWrapper st-page">
          <div className="matchCenterHeader">
            <div className="st-header">
              <StadiumCrumbs items={crumbs} />
            </div>
          </div>

          <div className="st-scroll">
            <div className="st-content">
              {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}
              <div className="st-block">
                {seasonForm}
              </div>
            </div>
          </div>

          <div className="st-actions">
            {canWrite ? (
            <button
              type="button"
              className="st-btn st-btn-primary"
              disabled={saving}
              onClick={handleSave}
            >
              <FiSave /> {saving ? t('trLoading') : t('trCreateSeason')}
            </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page tr-season-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={crumbs} />
            {leaguesAccess.canRead ? (
              <Link
                to={`/league_champions?country=${countryId}&league=${leagueId}&season=${seasonId}`}
                className="st-btn st-btn-secondary"
              >
                <FiAward />
                <span className="tr-btn-full">{t('navChampions')}</span>
                <span className="tr-btn-short">{t('navChampionsShort')}</span>
              </Link>
            ) : null}
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="tr-season-stack">
            <div className="tr-season-split">
              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('trStages')}</h2>
                <div className="dq-panel-body">
                  {loading ? (
                    <div className="st-loading">
                      <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                      <p>{t('trLoading')}</p>
                    </div>
                  ) : (
                    <ReorderableTable
                      tableClassName="st-table-two st-table-tr-stages"
                      columns={stageColumns}
                      items={stages}
                      getRowKey={(item) => item.id}
                      reorderMode={reorderMode}
                      onItemsChange={setStages}
                      getRowHref={(item) => (
                        item.id
                          ? `/tournaments/${countryId}/league/${leagueId}/season/${seasonId}/stage/${item.id}`
                          : null
                      )}
                      empty={<div className="st-empty"><p>{t('trEmptyStages')}</p></div>}
                    />
                  )}
                  {reorderMode ? <p className="st-hint">{t('stDragHint')}</p> : null}
                </div>
                <div className="st-actions">
                  {canWrite ? stageActions : null}
                </div>
              </section>

              <section className="st-block dq-panel">
                <h2 className="st-block-header">{t('trEditSeason')}</h2>
                <div className="dq-panel-body">
                  {loading ? (
                    <div className="st-loading">
                      <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                      <p>{t('trLoading')}</p>
                    </div>
                  ) : seasonForm}
                </div>
                <div className="st-actions">
                  {canWrite ? (
                  <>
                  <button
                    type="button"
                    className="st-btn st-btn-secondary"
                    disabled={loading || saving || moving}
                    onClick={openMoveModal}
                  >
                    <FiShuffle />
                    <span className="tr-btn-full">{t('trChangeLeague')}</span>
                    <span className="tr-btn-short">{t('trChangeLeagueShort')}</span>
                  </button>
                  <button
                    type="button"
                    className="st-btn st-btn-primary"
                    disabled={loading || saving}
                    onClick={handleSave}
                  >
                    <FiSave />
                    {saving ? t('trLoading') : (
                      <>
                        <span className="tr-btn-full">{t('stdSaveChanges')}</span>
                        <span className="tr-btn-short">{t('stdSaveChangesShort')}</span>
                      </>
                    )}
                  </button>
                  </>
                  ) : null}
                </div>
              </section>
            </div>

            {loading ? null : (
            <>
            <SeasonRoster
              leagueType={Number(form.league_type)}
              teams={roster}
              onTeamsChange={setRosterDraft}
              groupsState={groupsState}
              onGroupsChange={setGroupsDraft}
              champId={champId}
              loading={rosterLoading}
              rosterDirty={rosterDirty}
              onSaveRoster={saveRoster}
              onResetRoster={() => setRosterDraft(savedRoster)}
              groupsDirty={groupsDirty}
              onSaveGroups={saveGroups}
              onResetGroups={() => setGroupsDraft(savedGroups)}
              saving={savingRoster}
              canWrite={canWrite}
            />
            <SeasonActions
              leagueId={Number(leagueId)}
              seasonId={Number(seasonId)}
              champId={champId}
              leagueType={Number(form.league_type)}
              roster={roster}
              groups={groupsState.list}
              assignment={groupsState.assignment}
              onMessage={setMessage}
              onSeasonChanged={reloadSeasonTeams}
              canWrite={canWrite}
            />
            </>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        styles={{ width: 460, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={moveVisible}
        closeModal={closeMoveModal}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('trMoveSeasonTitle')}</div>
          <p>{t('trMoveSeasonCurrent', { league: leagueName || t('trEditLeague') })}</p>
          {moveLoading ? (
            <div className="st-loading">
              <Loader type="Oval" color="#3ad1a0" height={36} width={36} />
            </div>
          ) : moveLeagues.length ? (
            <div className="st-field" style={{ textAlign: 'left' }}>
              <label>{t('trMoveSeasonTarget')}</label>
              <select
                className="st-select"
                value={moveTargetId}
                disabled={moving}
                onChange={(event) => {
                  setMoveTargetId(event.target.value);
                  setMoveError('');
                }}
              >
                <option value="">{t('trMoveSeasonTargetPh')}</option>
                {moveLeagues.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.name || `#${item.id}`}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <p>{moveError || t('trMoveSeasonEmpty')}</p>
          )}
          {moveTargetLeague ? (
            <>
              <p>
                {t('trMoveSeasonConfirm', {
                  season: form.name.trim() || t('trEditSeason'),
                  from: leagueName || t('trEditLeague'),
                  to: moveTargetLeague.name || `#${moveTargetLeague.id}`,
                })}
              </p>
              <p className="st-photo-hint">{t('trMoveSeasonHint')}</p>
            </>
          ) : null}
          {moveError && moveLeagues.length ? (
            <p className="st-field-error">{moveError}</p>
          ) : null}
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={moving}
              onClick={closeMoveModal}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={moving || !moveTargetLeague || moveLoading}
              onClick={handleMoveSeason}
            >
              {moving ? t('trLoading') : t('trMoveSeasonSubmit')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
